import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@shared/ui/dialog";
import { Button } from "@shared/ui/button";
import { useToast } from "@shared/hooks/use-toast";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { useAddSopReading, useEditSopReading } from "../../../../hooks";
import { Loader2, Thermometer, Camera } from "lucide-react";

interface Reading {
  name: string;
  unit: string;
  minimum?: string;
  maximum?: string;
  isPhotoReq?: string;
}

interface ReadingFormData {
  name: string;
  unit: string;
  minimum: string;
  maximum: string;
  photoRequired: boolean;
}

interface AddReadingModalProps {
  isOpen: boolean;
  onClose: () => void;
  sopId: string;
}

interface EditReadingModalProps {
  isOpen: boolean;
  onClose: () => void;
  sopId: string;
  reading: Reading | null;
  readingIndex: number;
}

// Enhanced Add Reading Modal Component
export const AddReadingModal: React.FC<AddReadingModalProps> = ({
  isOpen,
  onClose,
  sopId,
}) => {
  const { toast } = useToast();
  const addReadingMutation = useAddSopReading();

  // Enhanced form fields configuration
  const formFields: FormField[] = useMemo(
    () => [
      {
        name: "name",
        type: "text",
        label: "Reading Name",
        placeholder: "Enter reading parameter name...",
        required: true,
        minLength: 2,
        maxLength: 100,
        helperText: "Provide a clear name for the reading parameter",
      },
      {
        name: "unit",
        type: "text",
        label: "Unit of Measurement",
        placeholder: "e.g., kWh, psi, °C, bar",
        required: true,
        minLength: 1,
        maxLength: 20,
        helperText: "Specify the unit of measurement for this reading",
      },
      {
        name: "minimum",
        type: "number",
        label: "Minimum Value",
        placeholder: "Enter minimum value",
        required: false,
        step: 0.01,
        helperText: "Minimum acceptable value (optional)",
      },
      {
        name: "maximum",
        type: "number",
        label: "Maximum Value",
        placeholder: "Enter maximum value",
        required: false,
        step: 0.01,
        helperText: "Maximum acceptable value (optional)",
      },
      {
        name: "photoRequired",
        type: "switch",
        label: "Photo Documentation Required",
        helperText: "Enable if this reading requires photo documentation",
        defaultValue: false,
      },
    ],
    []
  );

  // Create form instance using FormService
  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    validateOnMount: false,
    defaultValues: {
      name: "",
      unit: "",
      minimum: "",
      maximum: "",
      photoRequired: false,
    },
  });

  // Handle field changes
  const handleFieldChange = useCallback(
    (fieldName: string, value: any) => {
      // Custom validation for min/max relationship
      if (fieldName === "minimum" || fieldName === "maximum") {
        const currentValues = form.getValues();
        const min =
          fieldName === "minimum"
            ? parseFloat(value)
            : parseFloat(currentValues.minimum);
        const max =
          fieldName === "maximum"
            ? parseFloat(value)
            : parseFloat(currentValues.maximum);

        if (!isNaN(min) && !isNaN(max) && min > max) {
          toast({
            title: "Validation Warning",
            description: "Minimum value should not exceed maximum value",
            variant: "destructive",
          });
        }
      }
    },
    [form, toast]
  );

  // Handle form submission
  const handleSubmit = async (data: ReadingFormData) => {
    try {
      await addReadingMutation.mutateAsync({
        sopId,
        readingData: {
          name: data.name.trim(),
          unit: data.unit.trim(),
          minimum: data.minimum || undefined,
          maximum: data.maximum || undefined,
          is_photo_req: data.photoRequired ? "Yes" : "No",
        },
      });

      toast({
        title: "Success",
        description: `Reading "${data.name}" added successfully`,
        variant: "default",
      });

      FormService.resetForm(form, formFields);
      onClose();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to add reading parameter",
        variant: "destructive",
      });
    }
  };

  const handleReset = useCallback(() => {
    FormService.resetForm(form, formFields);
  }, [form, formFields]);

  const handleClose = useCallback(() => {
    FormService.resetForm(form, formFields);
    onClose();
  }, [form, formFields, onClose]);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      FormService.resetForm(form, formFields);
    }
  }, [isOpen, form, formFields]);

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Thermometer className="h-5 w-5 text-blue-600" />
            Add New Reading Parameter
          </DialogTitle>
          <DialogDescription>
            Add a new reading parameter to this SOP template. Readings are
            measurements that need to be taken during SOP execution.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 pr-4">
          <DynamicForm
            fields={formFields}
            form={form}
            onSubmit={handleSubmit}
            onReset={handleReset}
            onFieldChange={handleFieldChange}
            loading={addReadingMutation.isPending}
            gridColumns={2}
            responsiveColumns={{
              sm: 1,
              md: 2,
            }}
            config={{
              showResetButton: false,
              showSubmitButton: false,
              resetOnSubmit: false,
              classes: {
                form: "space-y-4",
                container: "space-y-2",
                label: "block text-sm font-medium text-gray-900 mb-1",
                input:
                  "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed",
                select:
                  "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white disabled:bg-gray-100 disabled:cursor-not-allowed",
                error: "text-sm text-red-600 mt-1",
                helperText: "text-xs text-muted-foreground mt-1",
                required: "text-red-500 ml-1",
              },
            }}
            renderCustomFooter={() => (
              <div className="flex justify-end gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={handleClose}
                  disabled={addReadingMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  onClick={form.handleSubmit(handleSubmit)}
                  disabled={addReadingMutation.isPending}
                >
                  {addReadingMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Adding Reading...
                    </>
                  ) : (
                    "Add Reading"
                  )}
                </Button>
              </div>
            )}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Enhanced Edit Reading Modal Component
export const EditReadingModal: React.FC<EditReadingModalProps> = ({
  isOpen,
  onClose,
  sopId,
  reading,
  readingIndex,
}) => {
  const { toast } = useToast();
  const editReadingMutation = useEditSopReading();

  // Form fields configuration (same as Add modal)
  const formFields: FormField[] = useMemo(
    () => [
      {
        name: "name",
        type: "text",
        label: "Reading Name",
        placeholder: "Enter reading parameter name...",
        required: true,
        minLength: 2,
        maxLength: 100,
        helperText: "Provide a clear name for the reading parameter",
      },
      {
        name: "unit",
        type: "text",
        label: "Unit of Measurement",
        placeholder: "e.g., kWh, psi, °C, bar",
        required: true,
        minLength: 1,
        maxLength: 20,
        helperText: "Specify the unit of measurement for this reading",
      },
      {
        name: "minimum",
        type: "number",
        label: "Minimum Value",
        placeholder: "Enter minimum value",
        required: false,
        step: 0.01,
        helperText: "Minimum acceptable value (optional)",
      },
      {
        name: "maximum",
        type: "number",
        label: "Maximum Value",
        placeholder: "Enter maximum value",
        required: false,
        step: 0.01,
        helperText: "Maximum acceptable value (optional)",
      },
      {
        name: "photoRequired",
        type: "switch",
        label: "Photo Documentation Required",
        helperText: "Enable if this reading requires photo documentation",
        defaultValue: false,
      },
    ],
    []
  );

  // Create form instance using FormService
  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    validateOnMount: false,
    defaultValues: {
      name: "",
      unit: "",
      minimum: "",
      maximum: "",
      photoRequired: false,
    },
  });

  // Populate form when reading changes
  useEffect(() => {
    if (reading && isOpen) {
      const populateData = {
        name: reading.name || "",
        unit: reading.unit || "",
        minimum: reading.minimum || "",
        maximum: reading.maximum || "",
        photoRequired: reading.isPhotoReq === "Yes",
      };

      FormService.populateForm(form, populateData, formFields);
    }
  }, [reading, isOpen, form, formFields]);

  // Handle field changes
  const handleFieldChange = useCallback(
    (fieldName: string, value: any) => {
      // Custom validation for min/max relationship
      if (fieldName === "minimum" || fieldName === "maximum") {
        const currentValues = form.getValues();
        const min =
          fieldName === "minimum"
            ? parseFloat(value)
            : parseFloat(currentValues.minimum);
        const max =
          fieldName === "maximum"
            ? parseFloat(value)
            : parseFloat(currentValues.maximum);

        if (!isNaN(min) && !isNaN(max) && min > max) {
          toast({
            title: "Validation Warning",
            description: "Minimum value should not exceed maximum value",
            variant: "destructive",
          });
        }
      }
    },
    [form, toast]
  );

  // Handle form submission
  const handleSubmit = async (data: ReadingFormData) => {
    if (!reading) return;

    try {
      await editReadingMutation.mutateAsync({
        sopId,
        existingValue: reading.name,
        readingData: {
          name: data.name.trim(),
          unit: data.unit.trim(),
          minimum: data.minimum || undefined,
          maximum: data.maximum || undefined,
          is_photo_req: data.photoRequired ? "Yes" : "No",
        },
      });

      toast({
        title: "Success",
        description: `Reading "${data.name}" updated successfully`,
        variant: "default",
      });

      onClose();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update reading parameter",
        variant: "destructive",
      });
    }
  };

  const handleReset = useCallback(() => {
    FormService.resetForm(form, formFields);
  }, [form, formFields]);

  const handleClose = useCallback(() => {
    FormService.resetForm(form, formFields);
    onClose();
  }, [form, formFields, onClose]);

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Thermometer className="h-5 w-5 text-blue-600" />
            Edit Reading Parameter
          </DialogTitle>
          <DialogDescription>
            Update the reading parameter details for this SOP template.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 pr-4">
          <DynamicForm
            fields={formFields}
            form={form}
            onSubmit={handleSubmit}
            onReset={handleReset}
            onFieldChange={handleFieldChange}
            loading={editReadingMutation.isPending}
            gridColumns={2}
            responsiveColumns={{
              sm: 1,
              md: 2,
            }}
            config={{
              showResetButton: false,
              showSubmitButton: false,
              resetOnSubmit: false,
              classes: {
                form: "space-y-4",
                container: "space-y-2",
                label: "block text-sm font-medium text-gray-900 mb-1",
                input:
                  "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed",
                select:
                  "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white disabled:bg-gray-100 disabled:cursor-not-allowed",
                error: "text-sm text-red-600 mt-1",
                helperText: "text-xs text-muted-foreground mt-1",
                required: "text-red-500 ml-1",
              },
            }}
            renderCustomFooter={() => (
              <div className="flex justify-end gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={handleClose}
                  disabled={editReadingMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  onClick={form.handleSubmit(handleSubmit)}
                  disabled={editReadingMutation.isPending}
                >
                  {editReadingMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Updating Reading...
                    </>
                  ) : (
                    "Update Reading"
                  )}
                </Button>
              </div>
            )}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};
